import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { serverErrorMessage } from "../lib/apiError";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { REFRESH_INTERVAL_MS, useCachedQuery } from "./useCachedQuery";
import type { AdminOpenClaw, AgentAccess, AiMode, AiStatus, OpenClaw, TenantHealth } from "../types/openclaw";
import type {
  CalendarPayload,
  EmailPayload,
  MessagingPayload,
} from "../types/onboarding";
import type {
  DeleteErrorCode,
  DeleteResult,
  ReassignErrorCode,
  ReassignResult,
} from "./useClaws";

export type AdminClawsStatus = "loading" | "ready" | "error";

export type LlmTokenErrorCode = "INVALID_STATE" | "PUSH_FAILED" | "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type LlmTokenResult =
  | { ok: true }
  | { ok: false; code: LlmTokenErrorCode; message: string };

export type McpTokenErrorCode = "INVALID_STATE" | "PUSH_FAILED" | "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type McpTokenResult =
  | { ok: true }
  | { ok: false; code: McpTokenErrorCode; message: string };

// GET /api/admin/claws/{id}/mcp-token. Metadata only — never the raw token.
// `prefix` is the cleartext first 8 chars for ops identification.
export type McpTokenStatusView = {
  configured: boolean;
  updatedAt: string | null;
  prefix: string | null;
};

// Body of PUT /api/admin/claws/{id}/ai-config. Full intended state of
// the tenant's AI configuration; backend computes the diff vs current
// and reconciles atomically (mode switch first -> provider keys ->
// flags -> single VM push if needed).
export type AiConfigPutBody = {
  // "claude" only re-saves a Claude tenant's own mode; ztc-core refuses a switch to it.
  mode: AiMode;
  aiEnabled: string;
  ztcManaged: boolean;
  customerKeys?: Record<string, string>;
  removedProviders?: string[];
  openrouterApiKey?: string;
};

export type AiConfigPutResult =
  | { ok: true }
  // `unconfirmed`: ztc-core's answer never arrived (an edge timeout or a dropped
  // connection), so the change may still be landing.
  | { ok: false; code: LlmTokenErrorCode; message: string; unconfirmed?: true };

export type UnlockErrorCode = "NOT_FOUND" | "ALREADY_UNLOCKED" | "UNAUTHORIZED" | "UNKNOWN";

export type UnlockResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: UnlockErrorCode; message: string };

export type LockErrorCode = "NOT_FOUND" | "ALREADY_LOCKED" | "UNAUTHORIZED" | "UNKNOWN";

export type LockResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: LockErrorCode; message: string };

// Stop (pause) / resume share the same error surface: 404 NOT_FOUND,
// 409 INVALID_STATE (already stopped / not stopped / not running).
export type StopResumeErrorCode = "NOT_FOUND" | "INVALID_STATE" | "UNAUTHORIZED" | "UNKNOWN";

export type StopResumeResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: StopResumeErrorCode; message: string };

export type CoachingSessionsErrorCode = "INVALID" | "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type CoachingSessionsResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: CoachingSessionsErrorCode; message: string };

export type UnlimitedEntitlementsErrorCode = "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type UnlimitedEntitlementsResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: UnlimitedEntitlementsErrorCode; message: string };

export type AgentAccessResult = { ok: true; claw: AdminOpenClaw } | { ok: false; message: string };

export type BonusSlotsErrorCode = "INVALID" | "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type BonusSlotsResult =
  | { ok: true; claw: AdminOpenClaw }
  | { ok: false; code: BonusSlotsErrorCode; message: string };

export type ResizeErrorCode = "INVALID" | "INVALID_STATE" | "NOT_FOUND" | "UNAUTHORIZED" | "UNKNOWN";

export type ResizeResult =
  | { ok: true }
  | { ok: false; code: ResizeErrorCode; message: string };

// GET /api/admin/claws/{id}/resize-schedule. Minutes are local wall-clock
// minute-of-day in `zone`; null start/end means no nightly window.
export type ResizeSchedule = {
  // False until a schedule row has been saved for this tenant.
  configured: boolean;
  enabled: boolean;
  upSize: string;
  downSize: string;
  downStartMinute: number | null;
  downEndMinute: number | null;
  weekendsDown: boolean;
  zone: string;
  lastOutcome: string | null;
  lastActionAt: string | null;
};

export type ResizeScheduleInput = Omit<ResizeSchedule, "configured" | "lastOutcome" | "lastActionAt">;

// GET /api/admin/claws/resize-config. The supported EC2 instance types plus the
// up/down defaults — the resize modals render their size dropdowns from this so
// the offered options can never drift from the backend allowlist.
export type ResizeConfig = {
  allowedTypes: string[];
  upDefault: string;
  downDefault: string;
};

export type OnboardingSubmissionView = {
  tenantId: string;
  submittedAt: string;
  timezone: string;
  agentName: string;
  personalities: ReadonlyArray<string>;
  personalityNotes: string | null;
  preferredName: string | null;
  businessDescription: string | null;
  agentBoundaries: string | null;
  preferredChannel: string | null;
  selectedWorkflow: string | null;
  aiSetupMethod: "managed-litellm" | "openrouter" | "byo-key" | "openai-subscription" | null;
  aiCustomDescription: string | null;
  openrouterKeyProvided: boolean;
  messaging: MessagingPayload;
  email: EmailPayload;
  calendar: CalendarPayload;
};

export type UseAdminClawsResult = {
  status: AdminClawsStatus;
  claws: ReadonlyArray<AdminOpenClaw>;
  error: string | null;
  refresh: () => Promise<void>;
  reassign: (tenantId: string, ownerEmail: string) => Promise<ReassignResult>;
  deleteClaw: (tenantId: string) => Promise<DeleteResult>;
  unlock: (tenantId: string) => Promise<UnlockResult>;
  lock: (tenantId: string) => Promise<LockResult>;
  // Pause (stop the EC2) / resume (start it). Mirror unlock: POST with no
  // body, returns the updated AdminClaw.
  stop: (tenantId: string) => Promise<StopResumeResult>;
  resume: (tenantId: string) => Promise<StopResumeResult>;
  setCoachingSessions: (tenantId: string, remaining: number) => Promise<CoachingSessionsResult>;
  // Toggles the comp/unlimited-entitlements flag (IP-353). PATCH returns the
  // updated AdminClaw, merged into the list like setCoachingSessions.
  setUnlimitedEntitlements: (tenantId: string, unlimited: boolean) => Promise<UnlimitedEntitlementsResult>;
  // standard or platform_admin; the server refuses platform_admin unless the owner is internal.
  setAgentAccess: (tenantId: string, agentAccess: AgentAccess) => Promise<AgentAccessResult>;
  // Sets the admin-granted bonus integration slots (stacked on the tier
  // baseline + purchased slots). PATCH returns the updated AdminClaw,
  // merged into the list like setCoachingSessions.
  setBonusSlots: (tenantId: string, slots: number) => Promise<BonusSlotsResult>;
  // Manual vertical resize (POST /api/admin/claws/{id}/resize -> 202). Async:
  // the box enters RESIZING (renders as "Provisioning") and the list refetches
  // so the tightened 3s poll picks up the transition back to RUNNING.
  resizeInstance: (tenantId: string, instanceType: string) => Promise<ResizeResult>;
  // Per-instance resize schedule (nightly/weekend downsize, driven by the
  // backend reconciler when enabled).
  getResizeSchedule: (tenantId: string) => Promise<ResizeSchedule | null>;
  saveResizeSchedule: (tenantId: string, body: ResizeScheduleInput) => Promise<ResizeResult>;
  // Supported instance types + up/down defaults for the resize modals' dropdowns.
  getResizeConfig: () => Promise<ResizeConfig | null>;
  getOnboardingSubmission: (tenantId: string) => Promise<OnboardingSubmissionView | null>;
  // Synchronous re-probe of one tenant's OpenClaw runtime health
  // (POST /api/admin/claws/{id}/health/refresh, up to 55s). Returns the fresh
  // health view or null on failure; the list refetches either way.
  refreshHealth: (tenantId: string) => Promise<TenantHealth | null>;
  // AI config
  getAiStatus: (tenantId: string) => Promise<AiStatus | null>;
  addCredential: (tenantId: string, provider: string, key: string) => Promise<LlmTokenResult>;
  removeCredential: (tenantId: string, provider: string) => Promise<LlmTokenResult>;
  setZtcManaged: (tenantId: string, enabled: boolean) => Promise<LlmTokenResult>;
  setAiEnabled: (tenantId: string, state: string) => Promise<LlmTokenResult>;
  // Switches an existing tenant's AI mode (litellm <-> openrouter). Mints
  // or blocks LiteLLM keys and pushes new env + provider config to the
  // VM. openrouterApiKey is only required when going TO openrouter.
  switchAiMode: (tenantId: string, mode: "litellm" | "openrouter" | "chatgpt", openrouterApiKey?: string) => Promise<LlmTokenResult>;
  // Atomic AI-config save. Sends the full intended state in one PUT;
  // backend reconciles. Replaces the granular addCredential /
  // removeCredential / setZtcManaged / setAiEnabled live writes for
  // the AI Config modal flow.
  applyAiConfig: (tenantId: string, body: AiConfigPutBody) => Promise<AiConfigPutResult>;
  // MCP gateway wiring. getMcpTokenStatus reads SSM presence + token prefix;
  // rotateMcpToken mints a fresh token + ensures DNS + pushes to the VM;
  // pushMcpToken re-delivers the existing token (DNS + push, no new token).
  getMcpTokenStatus: (tenantId: string) => Promise<McpTokenStatusView | null>;
  rotateMcpToken: (tenantId: string) => Promise<McpTokenResult>;
  pushMcpToken: (tenantId: string) => Promise<McpTokenResult>;
  // Kicks off an on-demand fleet disk sweep on the backend. started=false
  // means a sweep was already in flight (still fine — fresh data is coming).
  triggerDiskSweep: () => Promise<{ ok: boolean; started: boolean }>;
};

const ADMIN_URL = "/api/admin/claws";

type ServerError = { code: string; message: string };

const isReassignErrorCode = (code: string): code is ReassignErrorCode =>
  code === "CAP" || code === "DUPLICATE" || code === "NOT_FOUND" ||
  code === "INVALID" || code === "UNAUTHORIZED" || code === "UNKNOWN";

const isDeleteErrorCode = (code: string): code is DeleteErrorCode =>
  code === "NOT_FOUND" || code === "INVALID_STATE" || code === "UNAUTHORIZED" || code === "UNKNOWN";

const isLlmTokenErrorCode = (code: string): code is LlmTokenErrorCode =>
  code === "INVALID_STATE" || code === "PUSH_FAILED" || code === "NOT_FOUND" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isMcpTokenErrorCode = (code: string): code is McpTokenErrorCode =>
  code === "INVALID_STATE" || code === "PUSH_FAILED" || code === "NOT_FOUND" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isUnlockErrorCode = (code: string): code is UnlockErrorCode =>
  code === "NOT_FOUND" || code === "ALREADY_UNLOCKED" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isLockErrorCode = (code: string): code is LockErrorCode =>
  code === "NOT_FOUND" || code === "ALREADY_LOCKED" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isCoachingSessionsErrorCode = (code: string): code is CoachingSessionsErrorCode =>
  code === "INVALID" || code === "NOT_FOUND" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isBonusSlotsErrorCode = (code: string): code is BonusSlotsErrorCode =>
  code === "INVALID" || code === "NOT_FOUND" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isStopResumeErrorCode = (code: string): code is StopResumeErrorCode =>
  code === "NOT_FOUND" || code === "INVALID_STATE" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

const isResizeErrorCode = (code: string): code is ResizeErrorCode =>
  code === "INVALID" || code === "INVALID_STATE" || code === "NOT_FOUND" ||
  code === "UNAUTHORIZED" || code === "UNKNOWN";

// Admin-scoped mirror of useClaws:
//   - list(): GET /api/admin/claws (all tenants, ownerEmail surfaced)
//   - reassign(): POST /api/admin/claws/{id}/reassign
//   - deleteClaw(): DELETE /api/admin/claws/{id} (admin bypass of owner check)
//
// No SSE for the admin view - fetch-on-mount + refetch-after-action is
// sufficient at current tenant count and avoids a second live stream.
//
// To keep PROVISIONING -> RUNNING/FAILED transitions visible without
// requiring a manual refresh, the dwell-refresh interval tightens to
// PROVISIONING_POLL_MS while any tenant is PROVISIONING.
const PROVISIONING_POLL_MS = 3000;

const fetchAdminClaws = async (signal: AbortSignal): Promise<ReadonlyArray<AdminOpenClaw>> => {
  const res = await fetch(ADMIN_URL, { credentials: "include", signal });
  if (res.status === 200) return (await res.json()) as AdminOpenClaw[];
  if (res.status === 401 || res.status === 403) throw new Error("Admin permissions required.");
  throw new Error(`List failed (${res.status})`);
};

export const useAdminClaws = (): UseAdminClawsResult => {
  const [anyProvisioning, setAnyProvisioning] = useState<boolean>(false);

  const { status, data, error, refetch, mutate } = useCachedQuery<ReadonlyArray<AdminOpenClaw>>({
    cacheResource: ADMIN_QUERIES.claws.resource,
    queryKey: ADMIN_URL,
    fetcher: fetchAdminClaws,
    refreshIntervalMs: anyProvisioning ? PROVISIONING_POLL_MS : REFRESH_INTERVAL_MS,
  });
  const claws = data ?? [];
  const fetchList = refetch;

  useEffect(() => {
    // Tighten the poll while any instance is mid-transition: PROVISIONING
    // (first boot) OR RESIZING (a resize blip) - both resolve to RUNNING and
    // we want the table to pick that up without a manual refresh.
    setAnyProvisioning((data ?? []).some((c) => c.status === "PROVISIONING" || c.status === "RESIZING"));
  }, [data]);

  const refreshHealth = useCallback(async (tenantId: string): Promise<TenantHealth | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/health/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status !== 200) return null;
      return (await res.json()) as TenantHealth;
    } catch {
      return null;
    } finally {
      // The probe persists server-side even when the response is lost
      // (e.g. timeout) - refetch either way so the list reflects it.
      void fetchList();
    }
  }, [fetchList]);

  const reassign = useCallback(async (tenantId: string, ownerEmail: string): Promise<ReassignResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/reassign`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ ownerEmail }),
      });

      if (res.status === 200) {
        const claw = (await res.json()) as OpenClaw;
        void fetchList();
        return { ok: true, claw };
      }

      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }

      if (res.status === 400 || res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isReassignErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Reassign failed (${res.status})` };
      }

      return { ok: false, code: "UNKNOWN", message: `Reassign failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, [fetchList]);

  const deleteClaw = useCallback(async (tenantId: string): Promise<DeleteResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });

      if (res.status === 204) {
        mutate((prev) => prev.filter((c) => c.tenantId !== tenantId));
        return { ok: true };
      }

      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }

      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isDeleteErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Delete failed (${res.status})` };
      }

      return { ok: false, code: "UNKNOWN", message: `Delete failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const unlock = useCallback(async (tenantId: string): Promise<UnlockResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/unlock`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isUnlockErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Unlock failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Unlock failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const lock = useCallback(async (tenantId: string): Promise<LockResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/lock`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isLockErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Lock failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Lock failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  // Stop and resume are the same request shape against different paths, so
  // share one implementation. POST with no body; 200 returns the updated
  // AdminClaw (optimistically merged into the list).
  const stopOrResume = useCallback(async (
    tenantId: string,
    action: "stop" | "start",
  ): Promise<StopResumeResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isStopResumeErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Request failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Request failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const stop = useCallback((tenantId: string): Promise<StopResumeResult> =>
    stopOrResume(tenantId, "stop"), [stopOrResume]);

  const resume = useCallback((tenantId: string): Promise<StopResumeResult> =>
    stopOrResume(tenantId, "start"), [stopOrResume]);

  const setCoachingSessions = useCallback(async (
    tenantId: string,
    remaining: number,
  ): Promise<CoachingSessionsResult> => {
    try {
      const res = await fetch(
        `${ADMIN_URL}/${encodeURIComponent(tenantId)}/coaching-sessions`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ remaining }),
        },
      );
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 400 || res.status === 404) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isCoachingSessionsErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Save failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Save failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const setUnlimitedEntitlements = useCallback(async (
    tenantId: string,
    unlimited: boolean,
  ): Promise<UnlimitedEntitlementsResult> => {
    try {
      const res = await fetch(
        `${ADMIN_URL}/${encodeURIComponent(tenantId)}/unlimited-entitlements`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ unlimited }),
        },
      );
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 404) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return { ok: false, code: "NOT_FOUND", message: body?.message ?? "Instance not found." };
      }
      return { ok: false, code: "UNKNOWN", message: `Save failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, [mutate]);

  const setAgentAccess = useCallback(async (tenantId: string, agentAccess: AgentAccess): Promise<AgentAccessResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/agent-access`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ agentAccess }),
      });
      if (!res.ok) return { ok: false, message: await serverErrorMessage(res, `Save failed (${res.status})`) };
      const claw = (await res.json()) as AdminOpenClaw;
      mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
      return { ok: true, claw };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  }, [mutate]);

  const setBonusSlots = useCallback(async (
    tenantId: string,
    slots: number,
  ): Promise<BonusSlotsResult> => {
    try {
      const res = await fetch(
        `${ADMIN_URL}/${encodeURIComponent(tenantId)}/bonus-slots`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ slots }),
        },
      );
      if (res.status === 200) {
        const claw = (await res.json()) as AdminOpenClaw;
        mutate((prev) => prev.map((c) => (c.tenantId === claw.tenantId ? claw : c)));
        return { ok: true, claw };
      }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 400 || res.status === 404) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isBonusSlotsErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Save failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Save failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, [mutate]);

  const resizeInstance = useCallback(async (tenantId: string, instanceType: string): Promise<ResizeResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/resize`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ instanceType }),
      });
      if (res.status === 202) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 400 || res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        const code = body?.code ?? "UNKNOWN";
        const narrowed = isResizeErrorCode(code) ? code : "UNKNOWN";
        return { ok: false, code: narrowed, message: body?.message ?? `Resize failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Resize failed (${res.status})` };
    } catch (err) {
      return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
    }
  }, [fetchList]);

  const getResizeSchedule = useCallback(async (tenantId: string): Promise<ResizeSchedule | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/resize-schedule`, {
        credentials: "include",
      });
      if (res.status !== 200) return null;
      return (await res.json()) as ResizeSchedule;
    } catch {
      return null;
    }
  }, []);

  const saveResizeSchedule = useCallback(async (tenantId: string, body: ResizeScheduleInput): Promise<ResizeResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/resize-schedule`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify(body),
      });
      if (res.status === 200) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      if (res.status === 400 || res.status === 404 || res.status === 409) {
        const b = (await res.json().catch(() => null)) as ServerError | null;
        const code = b?.code ?? "UNKNOWN";
        return { ok: false, code: isResizeErrorCode(code) ? code : "UNKNOWN", message: b?.message ?? `Save failed (${res.status})` };
      }
      return { ok: false, code: "UNKNOWN", message: `Save failed (${res.status})` };
    } catch (err) {
      return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
    }
  }, [fetchList]);

  const getResizeConfig = useCallback(async (): Promise<ResizeConfig | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/resize-config`, { credentials: "include" });
      if (res.status !== 200) return null;
      return (await res.json()) as ResizeConfig;
    } catch {
      return null;
    }
  }, []);

  const getOnboardingSubmission = useCallback(async (tenantId: string): Promise<OnboardingSubmissionView | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/onboarding-submission`, {
        credentials: "include",
      });
      if (res.status !== 200) return null;
      return (await res.json()) as OnboardingSubmissionView;
    } catch {
      return null;
    }
  }, []);

  const getAiStatus = useCallback(async (tenantId: string): Promise<AiStatus | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/ai`, {
        credentials: "include",
      });
      if (res.status !== 200) return null;
      return (await res.json()) as AiStatus;
    } catch {
      return null;
    }
  }, []);

  const aiAction = useCallback(async (
    tenantId: string,
    path: string,
    method: string = "POST",
    body?: unknown,
  ): Promise<LlmTokenResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/ai/${path}`, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (res.status === 204) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      const err = (await res.json().catch(() => null)) as ServerError | null;
      const code = err?.code ?? "UNKNOWN";
      return { ok: false, code: isLlmTokenErrorCode(code) ? code : "UNKNOWN", message: err?.message ?? `Failed (${res.status})` };
    } catch (err) {
      return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
    }
  }, [fetchList]);

  const addCredential = useCallback((tenantId: string, provider: string, key: string): Promise<LlmTokenResult> =>
    aiAction(tenantId, "credentials", "POST", { provider, key }), [aiAction]);

  const removeCredential = useCallback((tenantId: string, provider: string): Promise<LlmTokenResult> =>
    aiAction(tenantId, `credentials/${encodeURIComponent(provider)}`, "DELETE"), [aiAction]);

  const setZtcManaged = useCallback((tenantId: string, enabled: boolean): Promise<LlmTokenResult> =>
    aiAction(tenantId, "ztc-managed", enabled ? "POST" : "DELETE"), [aiAction]);

  const setAiEnabled = useCallback((tenantId: string, state: string): Promise<LlmTokenResult> =>
    aiAction(tenantId, "enabled", "POST", { state }), [aiAction]);

  const switchAiMode = useCallback(async (
    tenantId: string,
    mode: "litellm" | "openrouter" | "chatgpt",
    openrouterApiKey?: string,
  ): Promise<LlmTokenResult> => {
    try {
      const body: Record<string, unknown> = { mode };
      if (openrouterApiKey) body.openrouterApiKey = openrouterApiKey;
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/ai-mode`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify(body),
      });
      if (res.status === 200) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      const err = (await res.json().catch(() => null)) as ServerError | null;
      const code = err?.code ?? "UNKNOWN";
      return {
        ok: false,
        code: isLlmTokenErrorCode(code) ? code : "UNKNOWN",
        message: err?.message ?? `Switch failed (${res.status})`,
      };
    } catch (err) {
      return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
    }
  }, [fetchList]);

  const applyAiConfig = useCallback(async (
    tenantId: string,
    body: AiConfigPutBody,
  ): Promise<AiConfigPutResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/ai-config`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify(body),
      });
      if (res.status === 200) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      const err = (await res.json().catch(() => null)) as ServerError | null;
      const code = err?.code ?? "UNKNOWN";
      return {
        ok: false,
        code: isLlmTokenErrorCode(code) ? code : "UNKNOWN",
        message: err?.message ?? `Save failed (${res.status})`,
        // A non-JSON response may come from the edge without confirming the save.
        ...(err === null ? { unconfirmed: true as const } : {}),
      };
    } catch (err) {
      return {
        ok: false,
        code: "UNKNOWN",
        message: err instanceof Error ? err.message : String(err),
        unconfirmed: true,
      };
    }
  }, [fetchList]);

  const getMcpTokenStatus = useCallback(async (tenantId: string): Promise<McpTokenStatusView | null> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/mcp-token`, {
        credentials: "include",
      });
      if (res.status !== 200) return null;
      return (await res.json()) as McpTokenStatusView;
    } catch {
      return null;
    }
  }, []);

  // Shared POST for rotate-mcp-token / push-mcp-token. Both return 204 on
  // success and the {code,message} envelope on failure (mapped by
  // ClawExceptionHandler). Refreshes the list so the row reflects any state.
  const mcpAction = useCallback(async (tenantId: string, path: string): Promise<McpTokenResult> => {
    try {
      const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(tenantId)}/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 204) { void fetchList(); return { ok: true }; }
      if (res.status === 401 || res.status === 403) {
        return { ok: false, code: "UNAUTHORIZED", message: "Admin permissions required." };
      }
      const err = (await res.json().catch(() => null)) as ServerError | null;
      const code = err?.code ?? "UNKNOWN";
      return {
        ok: false,
        code: isMcpTokenErrorCode(code) ? code : "UNKNOWN",
        message: err?.message ?? `Failed (${res.status})`,
      };
    } catch (err) {
      return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
    }
  }, [fetchList]);

  const triggerDiskSweep = useCallback(async (): Promise<{ ok: boolean; started: boolean }> => {
    try {
      const res = await fetch(`${ADMIN_URL}/disk-sweep`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (!res.ok) return { ok: false, started: false };
      const body = (await res.json()) as { started: boolean };
      return { ok: true, started: body.started };
    } catch {
      return { ok: false, started: false };
    }
  }, []);

  const rotateMcpToken = useCallback((tenantId: string): Promise<McpTokenResult> =>
    mcpAction(tenantId, "rotate-mcp-token"), [mcpAction]);

  const pushMcpToken = useCallback((tenantId: string): Promise<McpTokenResult> =>
    mcpAction(tenantId, "push-mcp-token"), [mcpAction]);

  return {
    status, claws, error, refresh: fetchList,
    reassign, deleteClaw,
    unlock, lock, stop, resume, setCoachingSessions, setUnlimitedEntitlements, setAgentAccess, setBonusSlots,
    resizeInstance,
    getResizeSchedule, saveResizeSchedule, getResizeConfig, getOnboardingSubmission,
    refreshHealth,
    getAiStatus, addCredential, removeCredential,
    setZtcManaged, setAiEnabled, switchAiMode, applyAiConfig,
    getMcpTokenStatus, rotateMcpToken, pushMcpToken,
    triggerDiskSweep,
  };
};
